import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { GasPumpIcon, ImageSquareIcon, XIcon, CheckCircleIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Polls /api/driver/pending-fuel-receipts every 60 seconds. When Motive
 * detects a fill-up on this driver's truck, this component pops up asking
 * the driver to upload the receipt (photo/PDF) with optional gallons + cost.
 * Dismiss button available for edge cases (wrong truck, no receipt kept).
 */
export default function FuelReceiptPrompt() {
  const [pending, setPending] = useState([]);
  const [openIdx, setOpenIdx] = useState(0);
  const [attachments, setAttachments] = useState([]);
  const [notes, setNotes] = useState("");
  const [gallons, setGallons] = useState("");
  const [cost, setCost] = useState("");
  const [station, setStation] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const poll = async () => {
    try {
      const { data } = await api.get("/driver/pending-fuel-receipts");
      setPending(Array.isArray(data) ? data : []);
    } catch { /* ignore — driver may not be logged in */ }
  };

  useEffect(() => {
    poll();
    const t = setInterval(poll, 60000);
    return () => clearInterval(t);
  }, []);

  const current = pending[openIdx] || null;

  const resetForm = () => { setAttachments([]); setNotes(""); setGallons(""); setCost(""); setStation(""); };

  const nextOrClose = () => {
    resetForm();
    // Refresh the list so the resolved event drops off
    poll();
    setOpenIdx(0);
  };

  const onFile = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`${file.name} exceeds 5MB`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data);
      }
      setAttachments((a) => [...a, ...uploaded]);
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!current) return;
    if (attachments.length === 0) {
      toast.error("Please attach at least one receipt photo or PDF");
      return;
    }
    setSaving(true);
    try {
      // Strip the big base64 blob before POST — server has it saved under
      // uploaded_files and we're referencing it by id/url. Keeps the request
      // payload well under any ingress limit.
      const slim = attachments.map((a) => ({
        id: a.id,
        url: a.url,
        name: a.name,
        type: a.type,
        size: a.size,
      }));
      await api.post(`/driver/fuel-receipts/${current.id}/upload`, {
        attachments: slim,
        notes,
        station,
        // Driver may enter litres for a Canadian truck; server stores gallons.
        gallons: gallons ? Number(gallons) / (isMetric ? 3.78541 : 1) : null,
        cost: cost ? Number(cost) : null,
      });
      toast.success("Receipt uploaded — thanks!");
      nextOrClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setSaving(false);
    }
  };

  const dismiss = async () => {
    if (!current) return;
    if (!window.confirm("Dismiss this fuel-up? You can still upload a receipt later from the Fuel history.")) return;
    setSaving(true);
    try {
      await api.post(`/driver/fuel-receipts/${current.id}/dismiss`);
      toast.info("Dismissed");
      nextOrClose();
    } catch (e) {
      toast.error("Failed to dismiss");
    } finally {
      setSaving(false);
    }
  };

  if (!current) return null;

  const isMetric = current.truck_unit_system === "metric";
  const volLabel = isMetric ? "L" : "gal";
  const estVol = current.estimated_gallons != null
    ? (isMetric ? (Number(current.estimated_gallons) * 3.78541).toFixed(1) : Number(current.estimated_gallons).toFixed(1))
    : null;

  const when = new Date(current.recorded_at).toLocaleString();
  const gauge = `${Math.round(current.before_pct || 0)}% → ${Math.round(current.after_pct || 0)}%`;

  return (
    <Dialog open={!!current} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <DialogContent className="max-w-lg rounded-sm" data-testid="fuel-receipt-prompt">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <GasPumpIcon size={22} weight="fill" className="text-emerald-600" />
            Fuel-up detected — upload your receipt?
          </DialogTitle>
        </DialogHeader>

        <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-sm">
          <div className="font-mono text-[10px] uppercase text-emerald-800 font-bold mb-1">/// Motive telemetry</div>
          <div className="flex items-center justify-between text-emerald-900">
            <span><b>Unit {current.truck_unit_number || "—"}</b> · {when}</span>
            <span className="font-mono font-bold">{gauge}</span>
          </div>
          {current.estimated_gallons != null && (
            <div className="text-xs text-emerald-800 mt-1 font-mono">Est. {estVol} {volLabel} added</div>
          )}
        </div>

        <div className="space-y-3 mt-3">
          <div>
            <Label className="text-xs font-bold">Receipt photo or PDF *</Label>
            <label className="mt-1 flex items-center gap-2 border border-dashed border-slate-300 rounded-sm px-3 py-4 cursor-pointer hover:bg-slate-50">
              <ImageSquareIcon size={18} weight="bold" className="text-blue-600" />
              <span className="text-sm text-slate-700">{uploading ? "Uploading…" : "Tap to add photos/PDFs"}</span>
              <input
                type="file"
                accept="image/*,application/pdf"
                multiple
                className="hidden"
                onChange={(e) => onFile(e.target.files)}
                data-testid="fuel-receipt-file"
              />
            </label>
            {attachments.length > 0 && (
              <div className="mt-2 grid grid-cols-3 gap-2">
                {attachments.map((a, i) => (
                  <div key={i} className="relative border border-slate-200 rounded-sm p-1 flex items-center gap-1">
                    <span className="text-[10px] font-mono truncate flex-1">{a.name || `file-${i + 1}`}</span>
                    <button
                      type="button"
                      onClick={() => setAttachments((prev) => prev.filter((_, idx) => idx !== i))}
                      className="text-red-600"
                    >
                      <XIcon size={12} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold">Actual {volLabel} (opt.)</Label>
              <Input
                type="number"
                step="0.01"
                value={gallons}
                onChange={(e) => setGallons(e.target.value)}
                placeholder={estVol ? `~${estVol}` : (isMetric ? "e.g. 297" : "e.g. 78.5")}
                className="mt-1 rounded-sm font-mono text-sm"
                data-testid="fuel-receipt-gallons"
              />
            </div>
            <div>
              <Label className="text-xs font-bold">Total cost $ (opt.)</Label>
              <Input
                type="number"
                step="0.01"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                placeholder="e.g. 312.40"
                className="mt-1 rounded-sm font-mono text-sm"
                data-testid="fuel-receipt-cost"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold">Station (opt.)</Label>
            <Input
              value={station}
              onChange={(e) => setStation(e.target.value)}
              placeholder="e.g. Pilot #421, Petro-Pass"
              className="mt-1 rounded-sm text-sm"
              data-testid="fuel-receipt-station"
            />
          </div>

          <div>
            <Label className="text-xs font-bold">Notes (opt.)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Station name, DEF added, etc."
              className="mt-1 rounded-sm text-sm"
              data-testid="fuel-receipt-notes"
            />
          </div>
        </div>

        <DialogFooter className="pt-3">
          <Button variant="outline" onClick={dismiss} disabled={saving} className="rounded-sm" data-testid="fuel-receipt-dismiss">
            Not my fill-up
          </Button>
          <Button
            onClick={submit}
            disabled={saving || attachments.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold"
            data-testid="fuel-receipt-submit"
          >
            <CheckCircleIcon size={16} weight="bold" className="mr-2" />
            {saving ? "Saving…" : "Upload receipt"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
